/**
 * KB-META
 * Purpose: Prompt user for Genus Media beta PIN.
 * Role: ui.component
 * Owner: Layout team; LastReviewed: 2025-11-13
 */
import { AfterViewInit, Component, ElementRef, QueryList, ViewChildren, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { GenusMediaAccessService } from './genus-media-access.service';

@Component({
  selector: 'app-beta-access-pin-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule],
  template: `
    <form class="pin-dialog-form" [formGroup]="form" (ngSubmit)="submit()">
      <p class="pin-dialog-description">
        Enter the beta access PIN to unlock Genus Media tools.
      </p>
      <label class="pin-dialog-label">4-digit PIN</label>
      <div class="pin-input-grid">
        <input
          *ngFor="let key of digitKeys; let i = index"
          #pinInput
          type="password"
          maxlength="1"
          autocomplete="off"
          inputmode="numeric"
          pattern="\\d"
          [formControlName]="key"
          class="p-inputtext p-component pin-dialog-input"
          pInputText
          (input)="handleInput($event, i)"
          (keydown)="handleKeyDown($event, i)"
          (paste)="handlePaste($event, i)"
        />
      </div>
      <small class="pin-dialog-error" *ngIf="pinControlsInvalid">
        Please enter all four digits.
      </small>
      <small class="pin-dialog-error" *ngIf="errorMessage">
        {{ errorMessage }}
      </small>
      <div class="pin-dialog-actions">
        <button type="button" pButton label="Cancel" class="p-button-text" (click)="cancel()"></button>
        <button type="submit" pButton label="Unlock" [disabled]="form.invalid"></button>
      </div>
    </form>
  `,
  styles: [`
    .pin-dialog-form {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding-top: 0.25rem;
    }
    .pin-dialog-description {
      margin: 0;
      color: var(--text-color-secondary, #6b7280);
      font-size: 0.9rem;
      line-height: 1.4;
    }
    .pin-dialog-label {
      font-weight: 600;
      font-size: 0.85rem;
    }
    .pin-input-grid {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      margin: 0.5rem 0 0.25rem;
    }
    .pin-dialog-input {
      width: 3rem;
      height: 3rem;
      text-align: center;
      font-size: 1.5rem;
      font-weight: 600;
      border-radius: 0.5rem;
      letter-spacing: 0;
    }
    .pin-dialog-input::-ms-reveal,
    .pin-dialog-input::-ms-clear {
      display: none;
    }
    .pin-dialog-input:focus {
      box-shadow: 0 0 0 2px var(--primary-color, #3b82f6);
    }
    .pin-dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
    .pin-dialog-error {
      color: var(--red-500, #ef4444);
      font-size: 0.8rem;
    }
  `]
})
export class BetaAccessPinDialogComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly ref = inject(DynamicDialogRef);
  private readonly accessService = inject(GenusMediaAccessService);

  readonly digitKeys = ['d0', 'd1', 'd2', 'd3'] as const;

  form = this.fb.nonNullable.group({
    d0: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    d1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    d2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    d3: ['', [Validators.required, Validators.pattern(/^\d$/)]]
  });

  @ViewChildren('pinInput') pinInputs!: QueryList<ElementRef<HTMLInputElement>>;

  errorMessage = '';

  get pinControlsInvalid(): boolean {
    return this.digitKeys.some(
      (key) => this.form.controls[key].invalid && (this.form.controls[key].dirty || this.form.controls[key].touched)
    );
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.focusInput(0));
  }

  handleInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const raw = (input.value || '').replace(/\D/g, '');
    const digit = raw.slice(-1);
    this.updateDigit(index, digit);
    if (digit) {
      if (index < this.digitKeys.length - 1) {
        this.focusInput(index + 1);
      } else {
        input.blur();
      }
    }
  }

  handleKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    if (key === 'Backspace') {
      if (this.getDigit(index)) {
        this.updateDigit(index, '');
      } else if (index > 0) {
        event.preventDefault();
        this.focusInput(index - 1);
        this.updateDigit(index - 1, '');
      }
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
      return;
    }

    if (key === 'ArrowRight' && index < this.digitKeys.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
      return;
    }

    if (!/^\d$/.test(key) && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent, index: number): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!text) {
      return;
    }
    event.preventDefault();
    const digits = text.replace(/\D/g, '').slice(0, this.digitKeys.length - index);
    if (!digits.length) {
      return;
    }
    this.digitKeys.slice(index).forEach((_, idx) => this.updateDigit(index + idx, ''));
    digits.split('').forEach((digit, i) => {
      const targetIndex = index + i;
      if (targetIndex < this.digitKeys.length) {
        this.updateDigit(targetIndex, digit);
      }
    });
    const nextIndex = index + digits.length;
    if (nextIndex < this.digitKeys.length) {
      this.focusInput(nextIndex);
    } else {
      this.focusInput(this.digitKeys.length - 1);
      const lastInput = this.pinInputs?.toArray()?.[this.digitKeys.length - 1]?.nativeElement;
      lastInput?.blur();
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please enter all four digits.';
      this.focusInput(0);
      return;
    }
    const pin = this.digitKeys.map((key) => this.form.controls[key].value).join('');
    const level = this.accessService.tryUnlock(pin);
    if (!level) {
      this.errorMessage = 'Invalid PIN. Please try again.';
      this.clearAllDigits();
      this.focusInput(0);
      return;
    }
    this.ref.close(level);
  }

  cancel(): void {
    this.ref.close(null);
  }

  private updateDigit(index: number, digit: string): void {
    const key = this.digitKeys[index];
    this.form.controls[key].setValue(digit, { emitEvent: false });
    const input = this.pinInputs?.get(index)?.nativeElement;
    if (input) {
      input.value = digit;
    }
    if (digit) {
      this.form.controls[key].markAsDirty();
      this.form.controls[key].markAsTouched();
      if (this.errorMessage) {
        this.errorMessage = '';
      }
    } else {
      this.form.controls[key].markAsPristine();
      this.form.controls[key].markAsUntouched();
    }
  }

  private getDigit(index: number): string {
    const key = this.digitKeys[index];
    return this.form.controls[key].value;
  }

  private focusInput(index: number): void {
    const input = this.pinInputs?.toArray()?.[index]?.nativeElement;
    if (input) {
      input.focus();
      input.select();
    }
  }

  private clearAllDigits(): void {
    this.digitKeys.forEach((_, i) => this.updateDigit(i, ''));
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }
}


